import { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { MailDomain } from '../types/models/MailDomain';
import type { ApiResponseMailDomainList } from '../types/api/ApiResponseMailDomain';

interface UseMailDomainsReturn {
  mailDomains: MailDomain[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useMailDomains = (): UseMailDomainsReturn => {
  const [mailDomains, setMailDomains] = useState<MailDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchMailDomains = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/emails/domains');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseMailDomainList;
      if (data.status === 'success' && data.data?.mail_domains) {
        setMailDomains(data.data.mail_domains);
      } else if (data.status === 'fail' || data.status === 'error') {
        setError(data.message ?? 'Failed to load mail domains');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching mail domains');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchMailDomains();
    }
  }, [auth.isAuthenticated, fetchMailDomains]);

  return { mailDomains, loading, error, refetch: fetchMailDomains };
};
